import React, { createContext, useContext, useMemo } from "react";
import { createStore, type StoreApi } from "zustand";
import { useStoreWithEqualityFn } from "zustand/traditional";
import { persist, type PersistStorage } from "zustand/middleware";
import {
  type LocalStorageChat,
  localStorageStateSchema,
  type LocalStorageState,
} from "../../../../../../../../lib/types/localStorageTypes";
import { Value } from "@deskpro-product/messenger-api-schemas/typebox";
import type { Message } from "@deskpro-product/messenger-api-schemas/messenger/api/conversation/entities";
import { ulid } from "ulid";
import { subDays, subHours } from "date-fns";
import type { UrlCacheableConfig } from "@deskpro-product/messenger-api-schemas/messenger/config";

type LocalStorage = LocalStorageState & {
  reset(): void;
  setWasClosed(wasClosed: boolean): void;
  setLocale(locale: string): void;
  setAuth(auth: string | undefined): void;
  setNotificationsDenied(): void;
  setLastVisitedRoute: (
    lastRouteVisited: NonNullable<LocalStorageState["lastRouteVisited"]>,
  ) => void;
  setLastVisitedTab: (lastVisitedTab: LocalStorageState["lastVisited"]) => void;
  setUnsentMessage(conversationId: string, message: Message): void;
  removeUnsentMessage(conversationId: string, messageIds: string[]): void;
  updateActiveConversation: (conversationId: string, setLastVisited?: boolean) => void;
  setUserConfirmed: (userConfirmed: LocalStorageState["userConfirmed"]) => void;
};

type LocalStorageStore = StoreApi<LocalStorage>;

export const LocalStorageContext = createContext<LocalStorageStore | null>(null);

const storage: PersistStorage<LocalStorageState> = {
  getItem: (name: string) => {
    const raw = localStorage.getItem(name);
    if (!raw) return null;
    const parsedState = JSON.parse(raw);
    return {
      state: Value.Decode(localStorageStateSchema, parsedState),
      version: 0,
    };
  },
  setItem: (name, value) => {
    localStorage.setItem(name, JSON.stringify(value.state));
  },
  removeItem: (name: string) => {
    localStorage.removeItem(name);
  },
};

function initialState(type: NonNullable<UrlCacheableConfig["platform"]>): LocalStorageState {
  return {
    userConfirmed: {
      isConfirmed: false,
    },
    locale: "en-US",
    device: {
      device_id: ulid(),
      type,
    },
  };
}

type Set = (
  partial:
    | LocalStorage
    | Partial<LocalStorage>
    | ((state: LocalStorage) => LocalStorage | Partial<LocalStorage>),
  replace?: boolean | undefined,
) => void;

function store(platform: NonNullable<UrlCacheableConfig["platform"]>) {
  return function (set: Set) {
    return {
      ...initialState(platform),
      reset: () => set(initialState(platform)),
      setWasClosed: (wasClosed: boolean) => set((partial) => ({ ...partial, wasClosed })),
      setLocale: (locale: string) => set((partial) => ({ ...partial, locale })),
      setAuth: (auth: string) => set((partial) => ({ ...partial, auth })),
      setNotificationsDenied: () =>
        set((partial) => ({ ...partial, notificationsDeniedAt: new Date().toISOString() })),
      setUnsentMessage: (conversationId: string, message: Message) =>
        set((partial) => {
          const existingConversations = partial.conversations || [];
          const conversation = existingConversations.find(
            (conversation) => conversation.id === conversationId,
          );
          const conversations = conversation
            ? existingConversations.map((conversation) => {
                return conversation.id === conversationId
                  ? {
                      ...conversation,
                      unsentMessages: {
                        ...conversation.unsentMessages,
                        [message.id]: message,
                      },
                    }
                  : conversation;
              })
            : [
                ...existingConversations,
                {
                  id: conversationId,
                  lastActive: new Date().toISOString(),
                  unsentMessages: { [message.id]: message },
                },
              ];
          return { ...partial, conversations };
        }),
      removeUnsentMessage: (conversationId: string, messageIds: string[]) => {
        set((partial) => {
          const existingConversations = partial.conversations || [];
          const conversation = existingConversations.find(
            (conversation) => conversation.id === conversationId,
          );
          const conversations = conversation
            ? existingConversations.map((conversation) => {
                return conversation.id === conversationId
                  ? {
                      ...conversation,
                      unsentMessages: Object.fromEntries(
                        Object.entries(conversation.unsentMessages || {}).filter(
                          ([id]) => !messageIds.includes(id),
                        ),
                      ),
                    }
                  : conversation;
              })
            : existingConversations;

          return { ...partial, conversations };
        });
      },
      setLastVisitedRoute: (lastRouteVisited: NonNullable<LocalStorageState["lastRouteVisited"]>) =>
        set((partial) => ({ ...partial, lastRouteVisited })),
      setLastVisitedTab: (lastVisitedTab: LocalStorageState["lastVisited"]) =>
        set((partial) => ({ ...partial, lastVisited: lastVisitedTab })),
      updateActiveConversation: (conversationId: string, setLastVisited = false) => {
        set((partial) => {
          const existingConversations = partial.conversations || [];
          const conversation = existingConversations.find(
            (conversation) => conversation.id === conversationId,
          );
          const conversations = conversation
            ? existingConversations.map((conversation) => {
                return conversation.id === conversationId
                  ? { ...conversation, lastActive: new Date().toISOString() }
                  : conversation;
              })
            : [
                ...existingConversations,
                { id: conversationId, lastActive: new Date().toISOString() },
              ];
          return {
            ...partial,
            conversations,
            ...(setLastVisited
              ? { lastVisited: { tab: "conversation", id: conversationId } }
              : undefined),
          };
        });
      },
      setUserConfirmed: (userConfirmed: LocalStorageState["userConfirmed"]) =>
        set((partial) => ({ ...partial, userConfirmed })),
    };
  };
}

export default function DeviceContext({
  name,
  platform,
  children,
  useLocalStorage = true,
}: {
  name: string;
  platform: NonNullable<UrlCacheableConfig["platform"]>;
  useLocalStorage?: boolean;
  children: React.ReactNode;
}) {
  const value = useMemo(
    () =>
      createStore<LocalStorage>()(
        useLocalStorage
          ? persist(store(platform), {
              name,
              storage,
            })
          : store(platform),
      ),
    [name, platform, useLocalStorage],
  );

  return <LocalStorageContext.Provider value={value}>{children}</LocalStorageContext.Provider>;
}

export function useDeviceContext<T>(
  selector: (state: LocalStorage) => T,
  equalityFn?: (a: T, b: T) => boolean,
): T {
  const store = useContext(LocalStorageContext);
  if (!store) {
    throw new Error("Missing LocalStorageContext.Provider");
  }
  return useStoreWithEqualityFn(store, selector, equalityFn);
}

export function useDevice() {
  return useDeviceContext((state) => state.device);
}

export function useWasClosed() {
  return useDeviceContext((state) => ({
    wasClosed: state.wasClosed,
    setWasClosed: state.setWasClosed,
  }));
}

export function useLocale() {
  return useDeviceContext((state) => ({ locale: state.locale, setLocale: state.setLocale }));
}

export function useAuth() {
  return useDeviceContext((state) => ({ auth: state.auth, setAuth: state.setAuth }));
}

export function useNotificationsDenied() {
  return useDeviceContext((state) => ({
    notificationsDeniedAt: state.notificationsDeniedAt,
    setNotificationsDenied: state.setNotificationsDenied,
  }));
}

function getUnsentMessages(state: LocalStorage, conversationId: string) {
  const conversations = state.conversations || [];
  const conversation = conversations.find((conversation) => conversation.id === conversationId);
  return conversation?.unsentMessages;
}

export function useUnsentMessages() {
  return useDeviceContext((state) => ({
    setUnsentMessage: state.setUnsentMessage,
    removeUnsentMessage: state.removeUnsentMessage,
    getUnsentMessages: getUnsentMessages.bind(null, state),
  }));
}

export function useUserConfirmed() {
  return useDeviceContext((state) => ({
    isUserConfirmed: state.userConfirmed.isConfirmed,
    setUserConfirmed: state.setUserConfirmed,
  }));
}

export function useReset() {
  return useDeviceContext((state) => state.reset);
}

export function useLastVisitedRoute() {
  return useDeviceContext((state) => ({
    lastRouteVisited: state.lastRouteVisited,
    setLastVisitedRoute: state.setLastVisitedRoute,
  }));
}

export function useLastVisitedTab() {
  return useDeviceContext((state) => ({
    lastVisitedTab: state.lastVisited,
    setLastVisitedTab: state.setLastVisitedTab,
  }));
}

export type AllActiveConversations = {
  /** conversations that have been active in the last hour, sorted with most recent first */
  activeConversations: LocalStorageChat[];
  /** conversations that have been active in the last 7 days, sorted with most recent first. Includes activeConversations */
  recentActiveConversations: LocalStorageChat[];
};

function getActiveConversations(state: LocalStorage): AllActiveConversations {
  const conversations = (state.conversations || []).map((conversation) => ({
    ...conversation,
    lastActive: new Date(conversation.lastActive),
  }));

  // Sort conversations with most recent first
  const sortedConversations = [...conversations].sort(
    (a, b) => b.lastActive.getTime() - a.lastActive.getTime(),
  );

  return sortedConversations.reduce<AllActiveConversations>(
    (acc, conversation) => {
      if (conversation.lastActive > subHours(new Date(), 1)) {
        acc.activeConversations.push(conversation);
      }
      if (conversation.lastActive > subDays(new Date(), 7)) {
        acc.recentActiveConversations.push(conversation);
      }
      return acc;
    },
    { activeConversations: [], recentActiveConversations: [] },
  );
}

function hasActiveConversation(state: LocalStorage): boolean {
  const { activeConversations } = getActiveConversations(state);
  const lastVisited = state.lastVisited;

  return (
    activeConversations.length > 0 &&
    lastVisited?.tab === "conversation" &&
    lastVisited.id === activeConversations[0].id
  );
}

export function useActiveConversation() {
  return useDeviceContext((state) => ({
    hasActiveConversation: hasActiveConversation(state),
    activeConversations: getActiveConversations(state),
    updateActiveConversation: state.updateActiveConversation,
  }));
}
